export interface ApiErrorOptions {
  /** Safe, intentional copy that may be shown directly to the user. */
  userMessage?: string | null;
  /** Original transport/runtime error retained for diagnostics. */
  cause?: unknown;
  /** Stable product/domain code when the API provides one. */
  code?: string;
}

/**
 * Error raised by the API layer.
 *
 * `message` is diagnostic and is never automatically considered safe UI
 * copy. API functions must opt in with `userMessage` when they have mapped a
 * response to an intentional, actionable instruction.
 */
export class ApiError extends Error {
  public status: number | undefined;
  public userMessage: string | null;
  public code: string | undefined;
  public cause: unknown;

  constructor(message: string, status?: number, options: ApiErrorOptions = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.userMessage = options.userMessage?.trim() || null;
    this.code = options.code;
    this.cause = options.cause;
  }

  static userActionable(
    message: string,
    status?: number,
    options: Omit<ApiErrorOptions, 'userMessage'> = {},
  ): ApiError {
    return new ApiError(message, status, { ...options, userMessage: message });
  }
}

/** A local validation/decision error the user can genuinely correct. */
export class UserActionableError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'UserActionableError';
  }
}

export function actionableMessage(err: unknown): string | null {
  if (err instanceof UserActionableError) {
    const message = err.message.trim();
    return message || null;
  }
  if (err instanceof ApiError) return err.userMessage;
  return null;
}

export function diagnosticCause(err: unknown): unknown {
  return err instanceof ApiError && err.cause != null ? err.cause : err;
}
